import { useEffect, useState } from 'react';
import { NavLink, Route, Routes, useLocation } from 'react-router-dom';
import { useApp } from './state/AppContext.js';
import { Today } from './routes/Today.js';
import { Transactions } from './routes/Transactions.js';
import { Insights } from './routes/Insights.js';
import { Review } from './routes/Review.js';
import { Settings } from './routes/Settings.js';
import { Capture } from './routes/Capture.js';
import { Import } from './routes/Import.js';
import { QuickAddPage } from './routes/QuickAdd.js';
import { PrivacyPage, ShortcutsGuide } from './routes/StaticPages.js';
import { Button } from './components/primitives.js';

interface TabDefinition {
  to: string;
  label: string;
  glyph: string;
  title: string;
}

const TABS: TabDefinition[] = [
  { to: '/', label: 'Today', glyph: '◎', title: 'Today' },
  { to: '/transactions', label: 'History', glyph: '≡', title: 'Transactions' },
  { to: '/insights', label: 'Insights', glyph: '◫', title: 'Insights' },
  { to: '/review', label: 'Review', glyph: '!', title: 'Review' },
  { to: '/settings', label: 'Settings', glyph: '⚙', title: 'Settings' },
];

const TITLES: Record<string, { title: string; subtitle?: string }> = {
  '/': { title: 'Today' },
  '/transactions': { title: 'History' },
  '/insights': { title: 'Insights' },
  '/review': { title: 'Review' },
  '/settings': { title: 'Settings' },
  '/import': { title: 'Import' },
  '/capture': { title: 'Confirm payment' },
  '/add': { title: 'Add a payment' },
  '/shortcuts': { title: 'iPhone Shortcuts' },
  '/privacy': { title: 'Privacy' },
};

export function App(): JSX.Element {
  const { reviewCount, toastState, dismissToast } = useApp();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // A route change starts at the top, which is what a native app does.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const heading = TITLES[location.pathname] ?? { title: 'What I Spent' };

  return (
    <div className="app-shell">
      <header className="app-header" data-scrolled={scrolled}>
        <div className="app-header__inner">
          <div>
            <h1 className="app-header__title">{heading.title}</h1>
            {heading.subtitle ? <p className="app-header__subtitle">{heading.subtitle}</p> : null}
          </div>
        </div>
      </header>

      <main className="app-main" id="main">
        <Routes>
          <Route path="/" element={<Today />} />
          <Route path="/transactions" element={<Transactions />} />
          <Route path="/insights" element={<Insights />} />
          <Route path="/review" element={<Review />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/import" element={<Import />} />
          <Route path="/capture" element={<Capture />} />
          <Route path="/add" element={<QuickAddPage />} />
          <Route path="/shortcuts" element={<ShortcutsGuide />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>

      <nav className="tab-bar" aria-label="Main">
        {TABS.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            end={tab.to === '/'}
            className="tab-bar__link"
            title={tab.title}
          >
            <span className="tab-bar__glyph" aria-hidden="true">
              {tab.glyph}
              {tab.to === '/review' && reviewCount > 0 ? (
                <span className="tab-bar__badge">{reviewCount > 99 ? '99+' : reviewCount}</span>
              ) : null}
            </span>
            <span>{tab.label}</span>
            {tab.to === '/review' && reviewCount > 0 ? (
              <span className="visually-hidden">{reviewCount} needing review</span>
            ) : null}
          </NavLink>
        ))}
      </nav>

      {toastState ? (
        <div className="toast" role="status" aria-live="polite">
          {toastState.message}
          {toastState.action ? (
            <button
              type="button"
              style={{
                marginLeft: 'var(--space-3)',
                color: 'var(--accent)',
                fontWeight: 700,
                minHeight: 'auto',
              }}
              onClick={() => {
                toastState.action?.onAction();
                dismissToast();
              }}
            >
              {toastState.action.label}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function NotFound(): JSX.Element {
  return (
    <div className="empty-state" style={{ marginTop: 'var(--space-6)' }}>
      <p className="empty-state__title">That page does not exist</p>
      <p className="empty-state__body">The link may be out of date.</p>
      <div className="empty-state__action">
        <Button variant="accent" onClick={() => window.location.assign('/')}>
          Go to Today
        </Button>
      </div>
    </div>
  );
}
